/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useEffect, useState } from 'react';
import { hooks, unstable } from '@broadleaf/admin-components';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { LinkWithMiniBrowser } from 'app/common/components';
import { errorPayPal } from 'app/paypal/components/PayPalView/state/actions';
import { validateAccountRedirectUrl } from 'app/paypal/components/PayPalView/utils/PayPalApiUtils';

import messages from './CreateAccount.messages';

const { useCancelTokenFactory } = hooks;
const { Spinner, useFormatMessage } = unstable;

/**
 * Component that renders the start on-boarding action. By default it will
 * request a link to the PayPal dashboard and redirect the user to there.
 *
 * @author Nathan Moore (nathandmoore)
 */
const CreateAccount = ({
  disabled = false,
  dismissed = false,
  dispatch,
  finish = false,
  payPalApi,
  state
}) => {
  const formatMessage = useFormatMessage();
  const [getCancelToken] = useCancelTokenFactory();
  const [url, setUrl] = useState(get(state, 'data.url'));

  useEffect(() => {
    if (state.isFetching || !isEmpty(url) || !dismissed || disabled) {
      return;
    }

    (async () => {
      let response;

      if (finish) {
        response = await payPalApi.accounts.finish({
          cancelToken: getCancelToken()
        });
      } else {
        response = await payPalApi.accounts.provision({
          cancelToken: getCancelToken()
        });
      }

      if (response === undefined) {
        // there was an error, which is handled separately
        setUrl('#');
        return;
      }

      if (validateAccountRedirectUrl(response.url)) {
        setUrl(response.url);
      } else {
        setUrl('#');
        dispatch(
          errorPayPal({
            type: 'INVALID_REDIRECT_URL',
            data: {
              globalErrors: [
                { reason: formatMessage(messages.redirectLinkError) }
              ]
            }
          })
        );
      }
    })();
    // eslint-disable-next-line
  }, [disabled, dismissed, state]);

  const label = finish
    ? formatMessage(messages.resumeConfigurationLabel)
    : formatMessage(messages.createAccountLabel);

  return (
    <>
      {state.isFetching && <Spinner className="tw-mr-2" color="info" />}
      <LinkWithMiniBrowser
        className="w-relative focus:tw-outline-none focus:tw-shadow-outline tw-ml-2 tw-flex tw-inline-flex tw-items-center tw-rounded tw-bg-green-700 tw-py-2 tw-px-4 tw-text-sm tw-text-white tw-shadow hover:tw-bg-green-800 hover:tw-text-white hover:tw-no-underline"
        url={url}
        disabled={!dismissed || disabled}
        height={600}
        width={600}
        target="PayPalFrame"
      >
        {label}
      </LinkWithMiniBrowser>
    </>
  );
};

CreateAccount.propTypes = {
  /** Whether the button is disabled */
  disabled: PropTypes.bool,
  /** Whether the tax acknowledgement is dismissed */
  dismissed: PropTypes.bool,
  /** Whether the account needs to be finished rather than provisioned */
  finish: PropTypes.bool,
  /** Interface for interacting with the PayPal API */
  payPalApi: PropTypes.shape({
    accounts: PropTypes.shape({
      create: PropTypes.func.isRequired
    }).isRequired
  }).isRequired
};

export default CreateAccount;
export { CreateAccount };
